import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, Card, Modal, Button } from 'react-bootstrap';
import { Hub, HubCapsule } from '@aws-amplify/core';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import config from '@/lib/config';
import { AccountInviteModel, ACTION_STATUS, ACTION_TYPES } from '@/lib/models';
import { publicService } from '@/lib/services';
import {
	useAlert,
	useAutoSignInContext,
	useHandleError,
	usePlatformsContext,
	useQuery,
	useRedirectUser,
} from '@/hooks';
import { AsyncPage, LoadingButton, OtpInput } from '@/components';
import { getEncryptedTokens, storeRedirectPath, trackAction, toErrorFields } from '@/lib/utils';
import { ReactComponent as SendIcon } from '@/assets/icons/send-icon.svg';
import { ReactComponent as ConfirmSendIcon } from '@/assets/icons/confirm-send-icon.svg';
import { ReactComponent as ExclamationIcon } from '@/assets/icons/exclamation-icon.svg';

const emailObfuscationRegex = /(\w)(.+?)(@)(\w)(.+?)(.\w+$)/gm;
const emailObfuscationSubstring = '$1***$3$4***$6';

const CodeModalDialog = (dialogProps: any) => (
	<Modal.Dialog
		{...dialogProps}
		style={{ maxWidth: 'none', width: 'fit-content', marginLeft: 'auto', marginRight: 'auto' }}
	/>
);

export const AcceptInvite = () => {
	const redirectUser = useRedirectUser();
	const { setAutoSignInUser, setEmailAddress, setPassword } = useAutoSignInContext();
	const { setPlatforms } = usePlatformsContext();
	const history = useHistory();
	const { inviteId } = useParams<{ inviteId: string }>();
	const query = useQuery();
	const sessionExpired = useMemo(() => query.get('sessionExpired') === 'true', [query]);

	const handleError = useHandleError();
	const { showAlert } = useAlert();

	const [user, setUser] = useState<CognitoUser>();
	const [userEmail, setUserEmail] = useState('N/A');
	const [showAlreadyAuthenticatedModal, setShowAlreadyAuthenticatedModal] = useState(false);
	const [invite, setInvite] = useState<AccountInviteModel>();
	const [passwordInputValue, setPasswordInputValue] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const [showCodeModal, setShowCodeModal] = useState(false);
	const [codeInputValue, setCodeInputValue] = useState('');
	const [codeMessage, setCodeMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

	// Display message after page reload (like from session expire).
	useEffect(() => {
		if (!sessionExpired) {
			return;
		}

		showAlert({
			variant: 'danger',
			children: () => {
				return <p className="mb-0 text-white">Session expired.</p>;
			},
		});
	}, [sessionExpired, showAlert]);

	const fetchData = useCallback(async () => {
		if (config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true') {
			try {
				const currentUser: CognitoUser = await Auth.currentAuthenticatedUser();
				const userAttributes = await Auth.userAttributes(currentUser);
				const emailAttributeValue =
					userAttributes.find((cognitoUserAttribute) => {
						return cognitoUserAttribute.Name === 'email';
					})?.Value ?? 'N/A';

				setUser(currentUser);
				setUserEmail(emailAttributeValue);
				setShowAlreadyAuthenticatedModal(true);
			} catch (error) {
				// Fail silently, and fetch the invite.
				const response = await publicService.getInvite(inviteId).fetch();
				setInvite(response);
			}
		} else {
			const response = await publicService.getInvite(inviteId).fetch();
			setInvite(response);
		}
	}, [inviteId]);

	async function handlePasswordFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		try {
			if (!invite) {
				throw new Error('invite is required.');
			}

			setIsLoading(true);
			const platformJson = JSON.stringify(invite.platforms.map((it) => it.platformId));
			if (config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true') {
				await Auth.signUp({
					username: invite.emailAddress,
					password: passwordInputValue,
					attributes: { 'custom:platform': platformJson },
					autoSignIn: {
						enabled: true,
					},
				});
			}

			setShowCodeModal(true);
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.INVITE_ACCEPTED,
				status: ACTION_STATUS.FAILURE,
				emailAddress: invite?.emailAddress,
				inviteId,
				...toErrorFields(error),
			});
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}

	async function handleSendANewCodeButtonClick() {
		try {
			if (!invite) {
				throw new Error('invite is required.');
			}

			setIsLoading(true);
			setCodeMessage(null);

			if (config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true') {
				await Auth.resendSignUp(invite.emailAddress);

				trackAction({
					actionType: ACTION_TYPES.INVITE_RESENT,
					status: ACTION_STATUS.SUCCESS,
					emailAddress: invite.emailAddress,
					inviteId,
				});
			}

			setCodeMessage({ kind: 'success', text: 'A new verification code has been sent to your mail.' });
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.INVITE_RESENT,
				status: ACTION_STATUS.FAILURE,
				emailAddress: invite?.emailAddress,
				inviteId,
				...toErrorFields(error),
			});
			setCodeMessage({ kind: 'error', text: (error as Error).message || 'Unable to send a new code.' });
		} finally {
			setIsLoading(false);
		}
	}

	async function submitCode(rawCode: string) {
		setIsLoading(true);
		setCodeMessage(null);

		if (config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true') {
			try {
				if (!invite) {
					throw new Error('invite is required.');
				}

				await Auth.confirmSignUp(invite.emailAddress, rawCode.replace(/\s/g, ''));

				// If confirmSignUp is successful, this will send an 'autoSignIn' event to the Auth Hub.
				// The Hub is responsible for directing the user at this point.
			} catch (error) {
				if ((error as any).name === 'UserNotFoundException') {
					window.location.replace(`${window.location.href}?sessionExpired=true`);
					return;
				}

				if ((error as any).name === 'NotAuthorizedException') {
					(error as any).message = 'This invite has already been accepted.';
				}

				trackAction({
					actionType: ACTION_TYPES.INVITE_ACCEPTED,
					status: ACTION_STATUS.FAILURE,
					emailAddress: invite?.emailAddress,
					inviteId,
					...toErrorFields(error),
				});
				setCodeMessage({
					kind: 'error',
					text: 'The code is invalid please try again or send a new code',
				});
				setIsLoading(false);
			}
		} else {
			try {
				if (!invite) {
					throw new Error('invite is required.');
				}

				const { platforms } = await publicService.accountInvite(invite.inviteId).fetch();
				setPlatforms(platforms);
				storeRedirectPath(null);

				trackAction({
					actionType: ACTION_TYPES.INVITE_ACCEPTED,
					status: ACTION_STATUS.SUCCESS,
					emailAddress: invite.emailAddress,
					inviteId,
				});

				showAlert({
					variant: 'success',
					children: () => {
						return <p className="mb-0 text-white">Account confirmed.</p>;
					},
				});
			} catch (error) {
				trackAction({
					actionType: ACTION_TYPES.INVITE_ACCEPTED,
					status: ACTION_STATUS.FAILURE,
					emailAddress: invite?.emailAddress,
					inviteId,
					...toErrorFields(error),
				});
				handleError(error);
				setIsLoading(false);
			}
		}
	}

	function handleCodeFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		submitCode(codeInputValue);
	}

	// AWS Amplify Hub handles listening to 'auth' events.
	// https://docs.amplify.aws/lib/auth/auth-events/q/platform/js/
	useEffect(() => {
		const handleHubAuthEvent = async ({ payload }: HubCapsule) => {
			const { event } = payload;

			// If a 'autoSignIn' event is detected, set the auto sign in user to the payload and direct the user to the sign-in page.
			// Thats where all the challange logic is for passwords / MFA / etc.
			if (event === 'autoSignIn') {
				if (!invite) {
					throw new Error('invite is required.');
				}

				const { platforms } = await publicService.accountInvite(invite.inviteId).fetch();
				setPlatforms(platforms);
				storeRedirectPath(null);

				trackAction({
					actionType: ACTION_TYPES.INVITE_ACCEPTED,
					status: ACTION_STATUS.SUCCESS,
					emailAddress: invite.emailAddress,
					inviteId: invite.inviteId,
				});

				showAlert({
					variant: 'success',
					children: () => {
						return <p className="mb-0 text-white">Account confirmed.</p>;
					},
				});
				setAutoSignInUser(payload.data);
				setEmailAddress(invite?.emailAddress ?? '');
				setPassword(passwordInputValue);

				history.push('/sign-in');
			} else if (event === 'autoSignIn_failure') {
				if (!invite) {
					throw new Error('invite is required.');
				}

				const { platforms } = await publicService.accountInvite(invite.inviteId).fetch();
				setPlatforms(platforms);
				storeRedirectPath(null);

				trackAction({
					actionType: ACTION_TYPES.INVITE_ACCEPTED,
					status: ACTION_STATUS.SUCCESS,
					emailAddress: invite.emailAddress,
					inviteId: invite.inviteId,
				});

				showAlert({
					variant: 'success',
					children: () => {
						return <p className="mb-0 text-white">Account confirmed.</p>;
					},
				});

				history.push('/sign-in');
			}
		};

		Hub.listen('auth', handleHubAuthEvent);
		return () => {
			Hub.remove('auth', handleHubAuthEvent);
		};
	}, [history, invite, passwordInputValue, setAutoSignInUser, setEmailAddress, setPassword, setPlatforms, showAlert]);

	const handleContinueButtonClick = useCallback(async () => {
		try {
			const encryptedTokens = await getEncryptedTokens(user);
			redirectUser(encryptedTokens);
		} catch (error) {
			handleError(error);
		}
	}, [handleError, redirectUser, user]);

	return (
		<AsyncPage fetchData={fetchData}>
			<Modal
				centered
				show={showAlreadyAuthenticatedModal}
				onHide={() => {
					return;
				}}
			>
				<Modal.Header>
					<Modal.Title>Already Authenticated</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="mb-0">
						You are already signed in as <span className="bold">{userEmail}</span>, do you want to continue?
					</p>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex justify-content-end align-items-center">
						<Button
							variant="link"
							className="mr-4"
							onClick={async () => {
								await Auth.signOut();
								trackAction({
									actionType: ACTION_TYPES.ACCOUNT_SIGNED_OUT,
									status: ACTION_STATUS.SUCCESS,
									emailAddress: userEmail !== 'N/A' ? userEmail : undefined,
								});
								const response = await publicService.getInvite(inviteId).fetch();
								setInvite(response);
								setShowAlreadyAuthenticatedModal(false);
							}}
						>
							Sign Out and Accept Invite
						</Button>
						{user && <Button onClick={handleContinueButtonClick}>Continue</Button>}
					</div>
				</Modal.Footer>
			</Modal>

			<Modal
				centered
				show={showCodeModal}
				dialogAs={CodeModalDialog}
				onHide={() => {
					return;
				}}
			>
				<Modal.Header>
					<Modal.Title>Confirm Your Account</Modal.Title>
				</Modal.Header>
				<Form onSubmit={handleCodeFormSubmit}>
					<Modal.Body style={{ paddingTop: 20, paddingBottom: 20 }}>
						<p className="mb-4">
							We have sent a code by email to{' '}
							{invite?.emailAddress.replace(emailObfuscationRegex, emailObfuscationSubstring)}. <br /> Be
							sure to check your spam folder as well.
						</p>
						<Form.Group className="mb-2">
							<Form.Label>Verification Code</Form.Label>
							<div className="d-flex align-items-center" style={{ gap: 24 }}>
								<OtpInput
									value={codeInputValue}
									onChange={(next) => {
										setCodeInputValue(next);
										if (codeMessage) setCodeMessage(null);
									}}
									onComplete={(code) => submitCode(code)}
									autoFocus
									name="verification-code"
									hasError={codeMessage?.kind === 'error'}
								/>
								<Button
									variant="link"
									onClick={handleSendANewCodeButtonClick}
									className="d-flex align-items-center p-0"
									style={{ gap: 8 }}
								>
									<SendIcon />
									Send a New Code
								</Button>
							</div>
							{codeMessage && (
								<div
									className={`d-flex align-items-center mt-2 mb-0 ${
										codeMessage.kind === 'error' ? 'text-danger' : 'text-dark'
									}`}
									style={{ gap: 8, fontSize: 14 }}
								>
									{codeMessage.kind === 'error' ? <ExclamationIcon /> : <ConfirmSendIcon />}
									<span>{codeMessage.text}</span>
								</div>
							)}
						</Form.Group>
					</Modal.Body>
					<Modal.Footer>
						<div className="d-flex align-items-center w-100" style={{ gap: 8 }}>
							<Button
								variant="outline-primary"
								className="d-flex justify-content-center align-items-center"
								style={{ flex: '1 1 0', minWidth: 0 }}
								disabled={isLoading}
								onClick={() => {
									setShowCodeModal(false);
									setCodeInputValue('');
									setCodeMessage(null);
								}}
							>
								Cancel
							</Button>
							<LoadingButton
								isLoading={isLoading}
								disabled={!codeInputValue}
								variant="primary"
								type="submit"
								className="d-flex justify-content-center align-items-center"
								style={{ flex: '1 1 0', minWidth: 0 }}
							>
								Confirm
							</LoadingButton>
						</div>
					</Modal.Footer>
				</Form>
			</Modal>

			<Container className="py-6">
				<Row className="mb-5">
					<Col md={{ span: 10, offset: 1 }} lg={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
						<h1 className="mb-0">Accept Invite</h1>
					</Col>
				</Row>
				<Row>
					<Col md={{ span: 10, offset: 1 }} lg={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
						<Card>
							<Card.Header>
								<Card.Title>Account Setup</Card.Title>
							</Card.Header>
							<Card.Body>
								<Form onSubmit={handlePasswordFormSubmit}>
									<Form.Group>
										<Form.Label>Email Address</Form.Label>
										<Form.Control type="text" value={invite?.emailAddress} readOnly disabled />
									</Form.Group>
									<Form.Group>
										<Form.Label>Password</Form.Label>
										<Form.Control
											type="password"
											value={passwordInputValue}
											onChange={(event) => {
												setPasswordInputValue(event.currentTarget.value);
											}}
											required
										/>
										<ul className="pt-2 pl-4 small">
											<li>One lowercase letter</li>
											<li>One uppercase letter</li>
											<li>One number</li>
											<li>One special character</li>
											<li>Eight characters minimum</li>
										</ul>
									</Form.Group>
									<div className="text-right">
										<LoadingButton
											isLoading={isLoading}
											type="submit"
											disabled={!passwordInputValue}
										>
											Submit
										</LoadingButton>
									</div>
								</Form>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		</AsyncPage>
	);
};
